import { useState, useEffect, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Mail,
    Send,
    Users,
    Eye,
    MousePointerClick,
    Plus,
    Trash2,
    Edit,
    BarChart3,
    Clock,
    CheckCircle2,
    XCircle,
    Loader2,
    RefreshCw,
    Search,
    ChevronLeft,
    Save,
    TestTube,
    Sparkles,
    Settings,
    Key,
    AlertCircle
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Campaign {
    id: string;
    name: string;
    subject: string;
    html_content: string;
    status: "draft" | "scheduled" | "sending" | "sent" | "failed";
    total_recipients: number;
    total_sent: number;
    total_opens: number;
    total_clicks: number;
    created_at: string;
    sent_at: string | null;
}

interface UserProfile {
    user_id: string;
    email: string;
    display_name: string | null;
    credits: number;
}

interface EmailSettings {
    id: string;
    resend_api_key: string | null;
    email_from: string;
    reply_to: string | null;
    tracking_domain: string | null;
    is_enabled: boolean;
    email_footer: string | null;
}

type ViewMode = "list" | "create" | "edit" | "analytics" | "settings";

const EmailMarketing = () => {
    const { toast } = useToast();
    const [viewMode, setViewMode] = useState<ViewMode>("list");
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);

    // Form state
    const [campaignName, setCampaignName] = useState("");
    const [subject, setSubject] = useState("");
    const [htmlContent, setHtmlContent] = useState("");
    const [testEmail, setTestEmail] = useState("");

    // Recipients
    const [users, setUsers] = useState<UserProfile[]>([]);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [userSearch, setUserSearch] = useState("");
    const [selectAll, setSelectAll] = useState(false);

    // Analytics
    const [analytics, setAnalytics] = useState<any>(null);

    // Email Settings
    const [emailSettings, setEmailSettings] = useState<EmailSettings | null>(null);
    const [settingsSaving, setSettingsSaving] = useState(false);
    const [settingsForm, setSettingsForm] = useState({
        resend_api_key: "",
        email_from: "GenCNX <noreply@gencnx.com>",
        reply_to: "",
        is_enabled: false,
        email_footer: "<p style='text-align: center; color: #666; font-size: 12px; margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee;'><strong>GenCNX Platform</strong><br>สร้างภาพ วิดีโอ และเพลง AI คุณภาพสูง<br><a href='https://www.gencnx.com' style='color: #666;'>www.gencnx.com</a></p>"
    });

    // Quill modules configuration
    const quillModules = useMemo(() => ({
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: [] }],
            ["link", "image"],
            ["blockquote", "code-block"],
            ["clean"],
        ],
    }), []);

    const quillFormats = [
        "header", "bold", "italic", "underline", "strike",
        "color", "background", "list", "bullet", "align",
        "link", "image", "blockquote", "code-block"
    ];

    // Fetch campaigns
    const fetchCampaigns = async () => {
        setLoading(true);
        try {
            const { data, error } = await (supabase as any)
                .from("email_campaigns")
                .select("*")
                .order("created_at", { ascending: false });

            if (error) throw error;
            setCampaigns((data || []) as Campaign[]);
        } catch (error: any) {
            console.error("Error fetching campaigns:", error);
            toast({
                title: "Error",
                description: "Failed to load campaigns",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch users for recipient selection (with pagination limit)
    const fetchUsers = async () => {
        try {
            const { data, error } = await supabase
                .from("profiles")
                .select("user_id, email, display_name, credits")
                .order("email")
                .limit(1000); // Limit to first 1000 users for performance

            if (error) throw error;
            setUsers((data || []) as UserProfile[]);
        } catch (error: any) {
            console.error("Error fetching users:", error);
        }
    };

    // Fetch email settings
    const fetchEmailSettings = async () => {
        try {
            const { data, error } = await (supabase as any)
                .from("email_settings")
                .select("*")
                .limit(1)
                .single();

            if (error && error.code !== 'PGRST116') throw error;

            if (data) {
                setEmailSettings(data as EmailSettings);
                setSettingsForm({
                    resend_api_key: data.resend_api_key || "",
                    email_from: data.email_from || "GenCNX <noreply@gencnx.com>",
                    reply_to: data.reply_to || "",
                    is_enabled: data.is_enabled || false,
                    email_footer: data.email_footer || "<p style='text-align: center; color: #666; font-size: 12px; margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee;'><strong>GenCNX Platform</strong><br>สร้างภาพ วิดีโอ และเพลง AI คุณภาพสูง<br><a href='https://www.gencnx.com' style='color: #666;'>www.gencnx.com</a></p>"
                });
            }
        } catch (error: any) {
            console.error("Error fetching email settings:", error);
        }
    };

    // Save email settings
    const saveEmailSettings = async () => {
        setSettingsSaving(true);
        try {
            const updateData = {
                resend_api_key: settingsForm.resend_api_key || null,
                email_from: settingsForm.email_from,
                reply_to: settingsForm.reply_to || null,
                is_enabled: settingsForm.is_enabled,
                email_footer: settingsForm.email_footer || null,
                updated_at: new Date().toISOString()
            };

            if (emailSettings?.id) {
                const { error } = await (supabase as any)
                    .from("email_settings")
                    .update(updateData)
                    .eq("id", emailSettings.id);
                if (error) throw error;
            } else {
                const { error } = await (supabase as any)
                    .from("email_settings")
                    .insert(updateData);
                if (error) throw error;
            }

            toast({ title: "Settings Saved", description: "Email settings have been updated" });
            fetchEmailSettings();
        } catch (error: any) {
            console.error("Error saving settings:", error);
            toast({
                title: "Error",
                description: "Failed to save email settings",
                variant: "destructive",
            });
        } finally {
            setSettingsSaving(false);
        }
    };

    useEffect(() => {
        fetchCampaigns();
        fetchUsers();
        fetchEmailSettings();
    }, []);

    // Filter users based on search
    const filteredUsers = users.filter(u =>
        u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
        (u.display_name?.toLowerCase() || "").includes(userSearch.toLowerCase())
    );

    // Handle select all
    useEffect(() => {
        if (selectAll) {
            setSelectedUserIds(filteredUsers.map(u => u.user_id));
        }
    }, [selectAll, filteredUsers]);

    // Reset form
    const resetForm = () => {
        setCampaignName("");
        setSubject("");
        setHtmlContent("");
        setSelectedUserIds([]);
        setSelectAll(false);
        setSelectedCampaign(null);
    };

    // Create/Update campaign
    const saveCampaign = async (sendNow = false) => {
        if (!campaignName || !subject || !htmlContent) {
            toast({
                title: "Missing Information",
                description: "Please fill in all required fields",
                variant: "destructive",
            });
            return;
        }

        if (!sendNow && selectedUserIds.length === 0) {
            toast({
                title: "No Recipients",
                description: "Please select at least one recipient",
                variant: "destructive",
            });
            return;
        }

        setLoading(true);
        try {
            let campaignId = selectedCampaign?.id;

            if (campaignId) {
                // Update existing
                const { error } = await (supabase as any)
                    .from("email_campaigns")
                    .update({
                        name: campaignName,
                        subject,
                        html_content: htmlContent,
                        updated_at: new Date().toISOString(),
                    })
                    .eq("id", campaignId);

                if (error) throw error;
            } else {
                // Create new
                const { data, error } = await (supabase as any)
                    .from("email_campaigns")
                    .insert({
                        name: campaignName,
                        subject,
                        html_content: htmlContent,
                        status: "draft",
                        total_recipients: selectedUserIds.length,
                    })
                    .select()
                    .single();

                if (error) throw error;
                campaignId = data.id;

                // Add recipients
                const recipients = selectedUserIds.map(userId => {
                    const user = users.find(u => u.user_id === userId);
                    return {
                        campaign_id: campaignId,
                        user_id: userId,
                        email: user?.email || "",
                        display_name: user?.display_name,
                        status: "pending",
                    };
                });

                const { error: recipientsError } = await (supabase as any)
                    .from("campaign_recipients")
                    .insert(recipients);

                if (recipientsError) throw recipientsError;
            }

            toast({
                title: "Success",
                description: sendNow ? "Campaign saved" : "Campaign saved successfully",
            });

            if (sendNow && campaignId) {
                await sendCampaign(campaignId);
            } else {
                fetchCampaigns();
                setViewMode("list");
                resetForm();
            }
        } catch (error: any) {
            console.error("Error saving campaign:", error);
            toast({
                title: "Error",
                description: error.message || "Failed to save campaign",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    // Send campaign
    const sendCampaign = async (campaignId: string) => {
        setSending(true);
        try {
            const { data, error } = await supabase.functions.invoke("send-campaign-email", {
                body: { campaign_id: campaignId },
            });

            if (error) throw error;

            toast({
                title: "Campaign Sent!",
                description: `Successfully sent to ${data.sent} recipients`,
            });

            fetchCampaigns();
            setViewMode("list");
            resetForm();
        } catch (error: any) {
            console.error("Error sending campaign:", error);
            toast({
                title: "Send Failed",
                description: error.message || "Failed to send campaign",
                variant: "destructive",
            });
        } finally {
            setSending(false);
        }
    };

    // Send test email
    const sendTestEmail = async () => {
        if (!testEmail || !subject || !htmlContent) {
            toast({
                title: "Missing Information",
                description: "Please fill in subject, content, and test email address",
                variant: "destructive",
            });
            return;
        }

        setSending(true);
        try {
            // Create temporary campaign for test
            const { data: tempCampaign, error: createError } = await (supabase as any)
                .from("email_campaigns")
                .insert({
                    name: `[TEST] ${campaignName || "Test Campaign"}`,
                    subject,
                    html_content: htmlContent,
                    status: "draft",
                    total_recipients: 1,
                })
                .select()
                .single();

            if (createError) throw createError;

            const { data, error } = await supabase.functions.invoke("send-campaign-email", {
                body: {
                    campaign_id: tempCampaign.id,
                    test_email: testEmail,
                },
            });

            if (error) throw error;

            // Delete temp campaign
            await (supabase as any).from("email_campaigns").delete().eq("id", tempCampaign.id);

            toast({
                title: "Test Email Sent!",
                description: `Check your inbox at ${testEmail}`,
            });
        } catch (error: any) {
            console.error("Error sending test email:", error);
            toast({
                title: "Test Failed",
                description: error.message || "Failed to send test email",
                variant: "destructive",
            });
        } finally {
            setSending(false);
        }
    };

    // View analytics
    const viewAnalytics = async (campaign: Campaign) => {
        setSelectedCampaign(campaign);
        setViewMode("analytics");

        try {
            const { data, error } = await (supabase as any).rpc("get_campaign_analytics", {
                p_campaign_id: campaign.id,
            });

            if (error) throw error;
            setAnalytics(data);
        } catch (error: any) {
            console.error("Error fetching analytics:", error);
        }
    };

    // Delete campaign
    const deleteCampaign = async (id: string) => {
        if (!confirm("Are you sure you want to delete this campaign?")) return;

        try {
            const { error } = await (supabase as any).from("email_campaigns").delete().eq("id", id);
            if (error) throw error;

            toast({ title: "Campaign deleted" });
            fetchCampaigns();
        } catch (error: any) {
            toast({
                title: "Error",
                description: "Failed to delete campaign",
                variant: "destructive",
            });
        }
    };

    // Edit campaign - load data into form
    const editCampaign = (campaign: Campaign) => {
        setSelectedCampaign(campaign);
        setCampaignName(campaign.name);
        setSubject(campaign.subject);
        setHtmlContent(campaign.html_content);
        setViewMode("edit");
    };

    // Get status badge
    const getStatusBadge = (status: string) => {
        const styles: Record<string, string> = {
            draft: "bg-gray-500/10 text-gray-500 border-gray-500/20",
            scheduled: "bg-blue-500/10 text-blue-500 border-blue-500/20",
            sending: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20 animate-pulse",
            sent: "bg-green-500/10 text-green-500 border-green-500/20",
            failed: "bg-red-500/10 text-red-500 border-red-500/20",
        };
        return <Badge className={styles[status] || styles.draft}>{status.toUpperCase()}</Badge>;
    };

    // ========================================
    // RENDER: Campaign List
    // ========================================
    if (viewMode === "list") {
        return (
            <Card variant="glass">
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <Mail className="h-5 w-5 text-primary" />
                            Email Marketing
                        </CardTitle>
                        <CardDescription>Create and send promotional emails to your customers</CardDescription>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setViewMode("settings")}>
                            <Settings className="h-4 w-4 mr-2" />
                            Settings
                        </Button>
                        <Button variant="outline" size="sm" onClick={fetchCampaigns} disabled={loading}>
                            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                            Refresh
                        </Button>
                        <Button onClick={() => { resetForm(); setViewMode("create"); }}>
                            <Plus className="h-4 w-4 mr-2" />
                            New Campaign
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {/* API Warning Banner */}
                    {(!emailSettings?.resend_api_key || !emailSettings?.is_enabled) && (
                        <div className="mb-4 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
                            <div>
                                <p className="font-medium text-yellow-500">Email sending disabled</p>
                                <p className="text-sm text-muted-foreground">
                                    {!emailSettings?.resend_api_key
                                        ? "Please configure your Resend API key in Settings to enable email sending."
                                        : "Email sending is currently disabled. Enable it in Settings to send campaigns."
                                    }
                                </p>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="mt-2 border-yellow-500/30 text-yellow-500 hover:bg-yellow-500/10"
                                    onClick={() => setViewMode("settings")}
                                >
                                    <Settings className="h-3 w-3 mr-2" />
                                    Configure Settings
                                </Button>
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : campaigns.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Mail className="h-12 w-12 mx-auto mb-4 opacity-30" />
                            <p>No campaigns yet. Create your first one!</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {campaigns.map((campaign) => (
                                <div
                                    key={campaign.id}
                                    className="p-4 rounded-xl bg-muted/30 border border-white/5 hover:bg-muted/50 transition-colors"
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="font-medium truncate">{campaign.name}</h3>
                                                {getStatusBadge(campaign.status)}
                                            </div>
                                            <p className="text-sm text-muted-foreground truncate mb-2">
                                                Subject: {campaign.subject}
                                            </p>
                                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                                <span className="flex items-center gap-1">
                                                    <Users className="h-3 w-3" />
                                                    {campaign.total_recipients} recipients
                                                </span>
                                                {campaign.status === "sent" && (
                                                    <>
                                                        <span className="flex items-center gap-1">
                                                            <Eye className="h-3 w-3" />
                                                            {campaign.total_opens} opens
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <MousePointerClick className="h-3 w-3" />
                                                            {campaign.total_clicks} clicks
                                                        </span>
                                                    </>
                                                )}
                                                <span className="flex items-center gap-1">
                                                    <Clock className="h-3 w-3" />
                                                    {new Date(campaign.created_at).toLocaleDateString("th-TH")}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {campaign.status === "sent" && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => viewAnalytics(campaign)}
                                                >
                                                    <BarChart3 className="h-4 w-4 mr-1" />
                                                    Analytics
                                                </Button>
                                            )}
                                            {campaign.status === "draft" && (
                                                <>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => editCampaign(campaign)}
                                                    >
                                                        <Edit className="h-4 w-4 mr-1" />
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => sendCampaign(campaign.id)}
                                                        disabled={sending}
                                                    >
                                                        <Send className="h-4 w-4 mr-1" />
                                                        Send
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => deleteCampaign(campaign.id)}
                                                    >
                                                        <Trash2 className="h-4 w-4 text-destructive" />
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        );
    }

    // ========================================
    // RENDER: Create/Edit Campaign
    // ========================================
    if (viewMode === "create" || viewMode === "edit") {
        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => { setViewMode("list"); resetForm(); }}>
                        <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h2 className="text-xl font-bold">
                            {viewMode === "edit" ? "Edit Campaign" : "Create New Campaign"}
                        </h2>
                        <p className="text-sm text-muted-foreground">Design your email and select recipients</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left: Editor */}
                    <div className="lg:col-span-2 space-y-4">
                        <Card variant="glass">
                            <CardHeader>
                                <CardTitle className="text-base">Campaign Details</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium mb-1 block">Campaign Name</label>
                                    <Input
                                        value={campaignName}
                                        onChange={(e) => setCampaignName(e.target.value)}
                                        placeholder="e.g., New Year Promotion 2026"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium mb-1 block">Email Subject</label>
                                    <Input
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        placeholder="e.g., 🎉 Special Offer: 50% Off Credits!"
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        <Card variant="glass">
                            <CardHeader>
                                <CardTitle className="text-base">Email Content</CardTitle>
                                <CardDescription>Design your email using the editor below</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="bg-white rounded-lg overflow-hidden">
                                    <ReactQuill
                                        theme="snow"
                                        value={htmlContent}
                                        onChange={setHtmlContent}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        className="min-h-[400px]"
                                        placeholder="Start writing your email content..."
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Test Email */}
                        <Card variant="glass">
                            <CardHeader>
                                <CardTitle className="text-base flex items-center gap-2">
                                    <TestTube className="h-4 w-4" />
                                    Send Test Email
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex gap-2">
                                    <Input
                                        value={testEmail}
                                        onChange={(e) => setTestEmail(e.target.value)}
                                        placeholder="your@email.com"
                                        type="email"
                                    />
                                    <Button
                                        variant="outline"
                                        onClick={sendTestEmail}
                                        disabled={sending || !testEmail}
                                    >
                                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                        <span className="ml-2">Send Test</span>
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right: Recipients */}
                    <div className="space-y-4">
                        <Card variant="glass">
                            <CardHeader>
                                <CardTitle className="text-base flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        <Users className="h-4 w-4" />
                                        Recipients
                                    </span>
                                    <Badge variant="outline">{selectedUserIds.length} selected</Badge>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        value={userSearch}
                                        onChange={(e) => setUserSearch(e.target.value)}
                                        placeholder="Search users..."
                                        className="pl-9"
                                    />
                                </div>

                                <label className="flex items-center gap-2 p-2 rounded-lg bg-primary/10 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={(e) => setSelectAll(e.target.checked)}
                                        className="rounded"
                                    />
                                    <span className="text-sm font-medium">Select All ({filteredUsers.length})</span>
                                </label>

                                <div className="max-h-[400px] overflow-y-auto space-y-1 pr-1">
                                    {filteredUsers.map((user) => (
                                        <label
                                            key={user.user_id}
                                            className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted/30 cursor-pointer transition-colors"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedUserIds.includes(user.user_id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedUserIds([...selectedUserIds, user.user_id]);
                                                    } else {
                                                        setSelectedUserIds(selectedUserIds.filter(id => id !== user.user_id));
                                                        setSelectAll(false);
                                                    }
                                                }}
                                                className="rounded"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm truncate">{user.email}</p>
                                                {user.display_name && (
                                                    <p className="text-xs text-muted-foreground truncate">{user.display_name}</p>
                                                )}
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Actions */}
                        <div className="flex flex-col gap-2">
                            <Button
                                onClick={() => saveCampaign(false)}
                                disabled={loading}
                                variant="outline"
                                className="w-full"
                            >
                                <Save className="h-4 w-4 mr-2" />
                                Save as Draft
                            </Button>
                            <Button
                                onClick={() => saveCampaign(true)}
                                disabled={loading || sending || selectedUserIds.length === 0}
                                className="w-full"
                            >
                                {sending ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Send className="h-4 w-4 mr-2" />
                                )}
                                Save & Send Now
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ========================================
    // RENDER: Analytics
    // ========================================
    if (viewMode === "analytics" && selectedCampaign) {
        const openRate = selectedCampaign.total_sent > 0
            ? ((selectedCampaign.total_opens / selectedCampaign.total_sent) * 100).toFixed(1)
            : "0";
        const clickRate = selectedCampaign.total_opens > 0
            ? ((selectedCampaign.total_clicks / selectedCampaign.total_opens) * 100).toFixed(1)
            : "0";

        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => setViewMode("list")}>
                        <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h2 className="text-xl font-bold">{selectedCampaign.name}</h2>
                        <p className="text-sm text-muted-foreground">Campaign Analytics</p>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card variant="glass">
                        <CardContent className="pt-6">
                            <div className="text-center">
                                <Users className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                                <p className="text-2xl font-bold">{selectedCampaign.total_recipients}</p>
                                <p className="text-xs text-muted-foreground">Total Recipients</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card variant="glass">
                        <CardContent className="pt-6">
                            <div className="text-center">
                                <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
                                <p className="text-2xl font-bold">{selectedCampaign.total_sent}</p>
                                <p className="text-xs text-muted-foreground">Delivered</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card variant="glass">
                        <CardContent className="pt-6">
                            <div className="text-center">
                                <Eye className="h-8 w-8 mx-auto mb-2 text-purple-500" />
                                <p className="text-2xl font-bold">{selectedCampaign.total_opens}</p>
                                <p className="text-xs text-muted-foreground">Opens ({openRate}%)</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card variant="glass">
                        <CardContent className="pt-6">
                            <div className="text-center">
                                <MousePointerClick className="h-8 w-8 mx-auto mb-2 text-amber-500" />
                                <p className="text-2xl font-bold">{selectedCampaign.total_clicks}</p>
                                <p className="text-xs text-muted-foreground">Clicks ({clickRate}%)</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Recent Events */}
                {analytics?.recent_events && (
                    <Card variant="glass">
                        <CardHeader>
                            <CardTitle className="text-base">Recent Activity</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                {analytics.recent_events.map((event: any, idx: number) => (
                                    <div
                                        key={idx}
                                        className="flex items-center gap-3 p-2 rounded-lg bg-muted/30"
                                    >
                                        {event.event_type === "open" ? (
                                            <Eye className="h-4 w-4 text-purple-500" />
                                        ) : (
                                            <MousePointerClick className="h-4 w-4 text-amber-500" />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm truncate">{event.email}</p>
                                            {event.link_url && (
                                                <p className="text-xs text-muted-foreground truncate">{event.link_url}</p>
                                            )}
                                        </div>
                                        <span className="text-xs text-muted-foreground">
                                            {new Date(event.created_at).toLocaleString("th-TH")}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        );
    }

    // ========================================
    // RENDER: Settings
    // ========================================
    if (viewMode === "settings") {
        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => setViewMode("list")}>
                        <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h2 className="text-xl font-bold">Email Settings</h2>
                        <p className="text-sm text-muted-foreground">Configure your email sending service</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* API Configuration */}
                    <Card variant="glass">
                        <CardHeader>
                            <CardTitle className="text-base flex items-center gap-2">
                                <Key className="h-4 w-4" />
                                Resend API Configuration
                            </CardTitle>
                            <CardDescription>
                                Get your API key from <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">resend.com</a>
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <label className="text-sm font-medium mb-1 block">Resend API Key</label>
                                <Input
                                    type="password"
                                    value={settingsForm.resend_api_key}
                                    onChange={(e) => setSettingsForm({ ...settingsForm, resend_api_key: e.target.value })}
                                    placeholder="re_xxxxxxxx..."
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    Your API key is stored securely and never exposed to clients.
                                </p>
                            </div>

                            <div>
                                <label className="text-sm font-medium mb-1 block">From Email</label>
                                <Input
                                    value={settingsForm.email_from}
                                    onChange={(e) => setSettingsForm({ ...settingsForm, email_from: e.target.value })}
                                    placeholder="GenCNX <noreply@gencnx.com>"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    Format: Name &lt;email@domain.com&gt;
                                </p>
                            </div>

                            <div>
                                <label className="text-sm font-medium mb-1 block">Reply-To Email (Optional)</label>
                                <Input
                                    value={settingsForm.reply_to}
                                    onChange={(e) => setSettingsForm({ ...settingsForm, reply_to: e.target.value })}
                                    placeholder="support@gencnx.com"
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Email Footer */}
                    <Card variant="glass" className="lg:col-span-2">
                        <CardHeader>
                            <CardTitle className="text-base flex items-center gap-2">
                                <Sparkles className="h-4 w-4" />
                                Email Footer
                            </CardTitle>
                            <CardDescription>
                                Customize the footer that appears at the bottom of all emails
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <label className="text-sm font-medium mb-1 block">Footer HTML Content</label>
                                <div className="bg-white rounded-lg overflow-hidden">
                                    <ReactQuill
                                        theme="snow"
                                        value={settingsForm.email_footer}
                                        onChange={(value) => setSettingsForm({ ...settingsForm, email_footer: value })}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        className="min-h-[200px]"
                                        placeholder="Enter your email footer content..."
                                    />
                                </div>
                                <p className="text-xs text-muted-foreground mt-2">
                                    This footer will be automatically added to the bottom of all campaign emails.
                                </p>
                            </div>

                            {/* Preview */}
                            <div>
                                <label className="text-sm font-medium mb-1 block">Preview:</label>
                                <div className="p-4 rounded-lg bg-muted/30 border border-white/10">
                                    <div dangerouslySetInnerHTML={{ __html: settingsForm.email_footer }} />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Email Status */}
                    <Card variant="glass">
                        <CardHeader>
                            <CardTitle className="text-base flex items-center gap-2">
                                <Mail className="h-4 w-4" />
                                Email Sending Status
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className={`p-4 rounded-xl flex items-center justify-between ${settingsForm.is_enabled && settingsForm.resend_api_key
                                ? 'bg-green-500/10 border border-green-500/30'
                                : 'bg-muted/30 border border-white/10'
                                }`}>
                                <div className="flex items-center gap-3">
                                    <div className={`h-3 w-3 rounded-full ${settingsForm.is_enabled && settingsForm.resend_api_key
                                        ? 'bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]'
                                        : 'bg-gray-500'
                                        }`} />
                                    <span className="font-medium">
                                        {settingsForm.is_enabled && settingsForm.resend_api_key
                                            ? 'Email sending is ENABLED'
                                            : 'Email sending is DISABLED'
                                        }
                                    </span>
                                </div>
                                <Button
                                    variant={settingsForm.is_enabled ? "outline" : "default"}
                                    size="sm"
                                    onClick={() => setSettingsForm({ ...settingsForm, is_enabled: !settingsForm.is_enabled })}
                                    disabled={!settingsForm.resend_api_key}
                                >
                                    {settingsForm.is_enabled ? 'Disable' : 'Enable'}
                                </Button>
                            </div>

                            {!settingsForm.resend_api_key && (
                                <div className="p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                                    <div className="flex gap-2">
                                        <AlertCircle className="h-4 w-4 text-yellow-500 shrink-0 mt-0.5" />
                                        <p className="text-xs text-muted-foreground">
                                            You must enter a Resend API key before you can enable email sending.
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div className="pt-4 border-t border-white/10">
                                <h4 className="text-sm font-medium mb-2">How to get started:</h4>
                                <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside">
                                    <li>Sign up at <a href="https://resend.com" target="_blank" className="text-primary hover:underline">resend.com</a></li>
                                    <li>Verify your sending domain</li>
                                    <li>Create an API key</li>
                                    <li>Paste it here and enable sending</li>
                                </ol>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Save Button */}
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setViewMode("list")}>
                        Cancel
                    </Button>
                    <Button onClick={saveEmailSettings} disabled={settingsSaving}>
                        {settingsSaving ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <Save className="h-4 w-4 mr-2" />
                        )}
                        Save Settings
                    </Button>
                </div>
            </div>
        );
    }

    return null;
};

export default EmailMarketing;
